import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowRight, Trash2, Repeat } from "lucide-react";
import { toast } from "sonner";

export default function RedirectManager() {
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // New Redirect Form
  const [oldUrl, setOldUrl] = useState("/");
  const [newUrl, setNewUrl] = useState("/");
  const [type, setType] = useState("301");

  useEffect(() => {
    fetchRedirects();
  }, []);

  const fetchRedirects = async () => {
    try {
      const res = await axios.get(`${API}/seo/redirects`, { withCredentials: true });
      setRedirects(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch redirect rules");
    } finally {
      setLoading(false);
    }
  };

  const handleAddRedirect = async () => {
    if (!oldUrl) return toast.error("Old URL is required");
    if (type !== "410" && !newUrl) return toast.error("New URL is required for redirects");
    if (type !== "410" && oldUrl === newUrl) return toast.error("Old and New URLs cannot be the same");
    
    setSaving(true);
    try {
      await axios.post(`${API}/seo/redirects`, {
        old_url: oldUrl,
        new_url: type === "410" ? "" : newUrl,
        type: parseInt(type)
      }, { withCredentials: true });
      
      toast.success("Redirect rule added successfully!");
      setOldUrl("/");
      setNewUrl("/");
      setType("301");
      fetchRedirects();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to add redirect rule");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRedirect = async (id) => {
    try {
      await axios.delete(`${API}/seo/redirects/${id}`, { withCredentials: true });
      toast.success("Redirect rule deleted");
      fetchRedirects();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete redirect");
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600 dark:text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Repeat className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          301 / 302 Redirect Manager
        </h3>
        <p className="text-sm text-gray-500 dark:text-[#94A3B8]">
          Manage URL redirects to prevent 404 errors and preserve SEO rankings when pages move or get deleted.
        </p>
      </div>

      <div className="bg-white dark:bg-[#0B101A] border border-gray-100 dark:border-white/[0.05] rounded-xl p-5 shadow-xs">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Add Redirect Rule</h4>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 space-y-1.5">
            <label className="text-xs text-gray-500 dark:text-[#94A3B8]">Old URL Path</label>
            <Input 
              value={oldUrl}
              onChange={e => setOldUrl(e.target.value)}
              placeholder="/old-course-page"
              className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-xl"
            />
          </div>
          <div className="flex items-center justify-center pb-2 md:pb-3 px-2">
            <ArrowRight className="w-4 h-4 text-gray-500 dark:text-[#64748B] hidden md:block" />
          </div>
          <div className="flex-1 space-y-1.5">
            <label className="text-xs text-gray-500 dark:text-[#94A3B8]">New URL Path</label>
            <Input 
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              placeholder={type === "410" ? "Not required for 410 Gone" : "/new-course-page"}
              disabled={type === "410"}
              className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white disabled:opacity-50 rounded-xl"
            />
          </div>
          <div className="w-full md:w-32 space-y-1.5">
            <label className="text-xs text-gray-500 dark:text-[#94A3B8]">Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-xl">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-[#0B101A] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white">
                <SelectItem value="301">301 (Permanent)</SelectItem>
                <SelectItem value="302">302 (Temporary)</SelectItem>
                <SelectItem value="410">410 (Gone / Deleted)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={handleAddRedirect} 
            disabled={saving}
            className="w-full md:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl shadow-xs"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Rule"}
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-[#0B101A] border border-gray-100 dark:border-white/[0.05] rounded-xl overflow-hidden shadow-xs">
        {redirects.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-[#94A3B8]">
            No redirect rules added yet.
          </div>
        ) : (
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left text-sm min-w-[500px]">
              <thead className="bg-gray-50 dark:bg-white/[0.02] border-b border-gray-100 dark:border-white/[0.05]">
                <tr>
                  <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">Old Path</th>
                  <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">New Path</th>
                  <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">Type</th>
                  <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8] text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                {redirects.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:bg-white/[0.01]">
                    <td className="p-4 text-gray-900 dark:text-white font-mono text-xs">{r.old_url}</td>
                    <td className="p-4 text-emerald-600 dark:text-emerald-400 font-mono text-xs flex items-center gap-2">
                      {r.type !== 410 && (
                        <>
                          <ArrowRight className="w-3 h-3 text-gray-500 dark:text-[#64748B]" />
                          {r.new_url}
                        </>
                      )}
                      {r.type === 410 && <span className="text-gray-500 dark:text-[#94A3B8] italic">Permanently Deleted</span>}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        r.type === 301 ? 'bg-orange-500/20 text-orange-400' : 
                        r.type === 410 ? 'bg-red-500/20 text-red-400' : 
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {r.type}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteRedirect(r.redirect_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
